import React, { useEffect, useMemo, useState } from 'react';
import { CodeBlock } from '../CodeBlock';
import { ExampleAuthWrapper } from '../ExampleAuthWrapper';
import { FluidForm } from '../../../shared';
import { SimpleFormProvider } from '../../fluidComponents/fluidForm/SimpleFormLoader';
import { FluidChat } from '../../fluidComponents/fluidForm/FluidChat.component';
import { useFluidForm, useFormInstance } from '../../fluidComponents/fluidForm/FormContext';
import { Segment } from 'semantic-ui-react';
import { DomUpdateHighlighter } from '../../fluidComponents/fluidForm/DomUpdateHighlighter';
function formatCurrency(n) {
    if (!isFinite(n))
        return '$0.00';
    return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
}
export const MortgageCalculatorPage = () => {
    const formConfig = {
        "key": "mortgage-calculator-form",
        "label": "Mortgage Calculator",
        "type": "form",
        "description": `This is a mortgage calculator form that is used to calculate the monthly payment of a mortgage.
    - Your first message should be "Hi! I can help you estimate your mortgage, let me know what you want to buy and I'll help you with the estimation." 
    - The user may provide the home price and down payment only.
    - Do not ask for each field, use current market values to set defauls for Interest Rate, Property Tax Rate, Insurance Annual and HOA Monthly.
    - Provide sensible default values for all the fields so that the calculator works.
    - The users may not be familiar with the terms, use your knowledge to explain the terms in a way that is easy to understand.
    - When the user asks "I want to buy a house for $XXX have $YYY in Savings, want to buy a house in San Francisco", you can use your knowledge to provide values of the fields instead of asking for each field one by one.
    - As interest rate default use 5.5%.
    - The formula definition is below, we can use it to explain the user how the calculation works in case they ask about it.
    -- Loan Amount = Home Price - Down Payment
    -- Monthly Payment = (Loan Amount * Interest Rate / 12) / (1 - (1 + Interest Rate / 12)^(-Term Years * 12))
    -- Monthly Property Tax = (Home Price * Property Tax Rate) / 12
    -- Monthly Insurance = Insurance Annual / 12
    -- Monthly HOA = HOA Monthly
    -- Monthly Total = Monthly Payment + Monthly Property Tax + Monthly Insurance + Monthly HOA
    `,
        "fields": [
            { "key": "homePrice", "label": "Home Price", "type": "number", required: true },
            { "key": "downPayment", "label": "Down Payment", "type": "number", required: true },
            { "key": "interestRate", "label": "Interest Rate (APR %)", "type": "number", required: true },
            { "key": "termYears", "label": "Term (years)", "type": "number", required: true },
            { "key": "propertyTaxRate", "label": "Property Tax Rate (% / year)", "type": "number", required: true },
            { "key": "insuranceAnnual", "label": "Insurance (per year)", "type": "number", required: true },
            { "key": "hoaMonthly", "label": "HOA (per month)", "type": "number", required: true },
        ]
    };
    const form = new FluidForm(formConfig, {}, { styleConfig: {
            inputModes: ["speech", "camera", "fileUpload", "urlCapture"]
        } });
    return React.createElement(ExampleAuthWrapper, { formConfig: formConfig },
        React.createElement("header", null,
            React.createElement("h1", { className: "text-xl font-semibold text-gray-900" }, "Converting your existing form to a FluidForm"),
            React.createElement("p", { className: "text-gray-600  mt-1" },
                "This example shows you  how you can easily convert your existing form to a FluidForm and get AI capabilities out of the box. ",
                React.createElement("br", null),
                "All you have to do is to wrap your existing form in ",
                React.createElement("strong", null, "<FluidForm />"),
                " web component, specify the schema of the form and the capabilities you want to use.",
                React.createElement("br", null))),
        React.createElement("section", { className: "space-y-3 mt-4" },
            React.createElement("h2", null, "Settup Overview"),
            React.createElement("div", { className: "" },
                React.createElement(CodeBlock, { language: "html", githubUrl: "https://github.com/fluidform-ai/fluid-form/blob/main/examples/pages/MortgageCalculator.tsx", code: `
  <FluidForm 
     token="xxx-xxx-xxx-xxx"
     schema="{
      type: 'form', 
      key: 'mortgage-calculator-form', 
       fields: [{key: 'homePrice', label: 'Home Price', type: 'number'} ... etc]
      }"
      chat='{"position": "right"}'
      voice
      camera
      fileUpload
      onDataReceived={(data) => {
        document.getElementById('homePrice').value = data.homePrice;
        document.getElementById('downPayment').value = data.downPayment;
        ... etc ...
      }}
    >
      <div> ... Your existing Form Code... </div>
    </FluidForm>
` }))),
        React.createElement(Segment, null,
            React.createElement(SimpleFormProvider, { form: form, submissionId: "mortgage-calculator", render: (formProps, onSubmit, validationResults) => (React.createElement("div", null,
                    React.createElement(MortageCalculatorWithAi, null))) })));
};
function MortageCalculatorWithAi() {
    const form = useFluidForm();
    const instanceId = useFormInstance();
    const [data, setData] = useState(form.getFormSubmission());
    const handleMessageReceived = (message) => {
        form.mergeSubmissionData(message.data);
        let firstField = message.messageFields?.[0];
        setData(form.getFormSubmission());
        if (firstField) {
            form.setCurrentField(firstField);
        }
    };
    return (React.createElement("div", { className: "grid grid-cols-1 lg:grid-cols-2 gap-6" },
        React.createElement("div", null,
            React.createElement(DomUpdateHighlighter, null,
                React.createElement(MortgageCalculator, { data: form.getFormSubmission() }))),
        React.createElement("div", { className: "rounded-lg border border-gray-200 bg-white p-3" },
            React.createElement(FluidChat, { formKey: form.getFormKey(), instanceId: instanceId, initialMessage: "Hi! I can help you estimate your mortgage. You can ask me to set sensible defaults.", title: "Assistant", onMessageReceived: handleMessageReceived, rounded: true, inputModes: ["speech", "camera", "fileUpload", "urlCapture"], autoStartForm: true }))));
}
export function MortgageCalculator({ data }) {
    const [formData, setFormData] = useState(data);
    const handleChange = (key, value) => {
        setFormData({ ...formData, [key]: value });
    };
    useEffect(() => {
        setFormData(data);
    }, [data]);
    const { loanAmount, monthlyPI, monthlyTax, monthlyInsurance, monthlyHOA, monthlyTotal, } = useMemo(() => {
        const loanAmountCalc = Math.max(0, formData.homePrice - formData.downPayment);
        const r = (formData.interestRate / 100) / 12;
        const n = formData.termYears * 12;
        const monthlyPIcalc = r > 0
            ? (loanAmountCalc * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1)
            : (n > 0 ? loanAmountCalc / n : 0);
        const monthlyTaxCalc = (formData.homePrice * (formData.propertyTaxRate / 100)) / 12;
        const monthlyInsuranceCalc = formData.insuranceAnnual / 12;
        const monthlyHOAcalc = formData.hoaMonthly;
        const monthlyTotalCalc = monthlyPIcalc + monthlyTaxCalc + monthlyInsuranceCalc + monthlyHOAcalc;
        return {
            loanAmount: loanAmountCalc,
            monthlyPI: monthlyPIcalc,
            monthlyTax: monthlyTaxCalc,
            monthlyInsurance: monthlyInsuranceCalc,
            monthlyHOA: monthlyHOAcalc,
            monthlyTotal: monthlyTotalCalc,
        };
    }, [formData]);
    return (React.createElement("div", { className: "space-y-5" },
        React.createElement("header", null,
            React.createElement("h1", { className: "text-xl font-semibold text-gray-900" }, "Mortgage Calculator"),
            React.createElement("p", { className: "text-gray-600 text-sm mt-1" }, "Adjust the parameters to estimate your monthly payment (Principal & Interest, Taxes, Insurance, and HOA).")),
        React.createElement("div", { className: "space-y-5" },
            React.createElement("section", { className: "space-y-3" },
                React.createElement("div", { className: "rounded-lg border border-gray-200 bg-white p-4" },
                    React.createElement("div", { className: "text-sm font-semibold text-gray-900 mb-2" }, "Estimated Monthly Payment"),
                    React.createElement("div", { className: "space-y-1 text-sm" },
                        React.createElement("div", { className: "flex items-center justify-between" },
                            React.createElement("span", { className: "text-gray-600" }, "Principal & Interest"),
                            React.createElement("span", { className: "font-medium" }, formatCurrency(monthlyPI))),
                        React.createElement("div", { className: "flex items-center justify-between" },
                            React.createElement("span", { className: "text-gray-600" }, "Property Tax"),
                            React.createElement("span", { className: "font-medium" }, formatCurrency(monthlyTax))),
                        React.createElement("div", { className: "flex items-center justify-between" },
                            React.createElement("span", { className: "text-gray-600" }, "Home Insurance"),
                            React.createElement("span", { className: "font-medium" }, formatCurrency(monthlyInsurance))),
                        React.createElement("div", { className: "flex items-center justify-between" },
                            React.createElement("span", { className: "text-gray-600" }, "HOA"),
                            React.createElement("span", { className: "font-medium" }, formatCurrency(monthlyHOA))),
                        React.createElement("div", { className: "border-t border-gray-200 my-2" }),
                        React.createElement("div", { className: "flex items-center justify-between text-base" },
                            React.createElement("span", { className: "text-gray-900 font-semibold" }, "Total"),
                            React.createElement("span", { className: "font-semibold" }, formatCurrency(monthlyTotal)))))),
            React.createElement("section", { className: "space-y-3" },
                React.createElement("div", { className: "grid grid-cols-2 gap-3" },
                    React.createElement("label", { className: "text-sm text-gray-700" },
                        React.createElement("span", { className: "block text-xs text-gray-500 mb-1" }, "Home Price"),
                        React.createElement("input", { type: "number", value: formData.homePrice, onChange: (e) => handleChange('homePrice', Number(e.target.value)), className: "w-full border border-gray-300 rounded px-2 py-1 text-sm", min: 0, step: 1000 })),
                    React.createElement("label", { className: "text-sm text-gray-700" },
                        React.createElement("span", { className: "block text-xs text-gray-500 mb-1" }, "Down Payment"),
                        React.createElement("input", { type: "number", value: formData.downPayment, onChange: (e) => handleChange('downPayment', Number(e.target.value)), className: "w-full border border-gray-300 rounded px-2 py-1 text-sm", min: 0, step: 1000 })),
                    React.createElement("label", { className: "text-sm text-gray-700" },
                        React.createElement("span", { className: "block text-xs text-gray-500 mb-1" }, "Interest Rate (APR %)"),
                        React.createElement("input", { type: "number", value: formData.interestRate, onChange: (e) => handleChange('interestRate', Number(e.target.value)), className: "w-full border border-gray-300 rounded px-2 py-1 text-sm", min: 0, step: 0.01 })),
                    React.createElement("label", { className: "text-sm text-gray-700" },
                        React.createElement("span", { className: "block text-xs text-gray-500 mb-1" }, "Term (years)"),
                        React.createElement("input", { type: "number", value: formData.termYears, onChange: (e) => handleChange('termYears', Number(e.target.value)), className: "w-full border border-gray-300 rounded px-2 py-1 text-sm", min: 1, step: 1 })),
                    React.createElement("label", { className: "text-sm text-gray-700" },
                        React.createElement("span", { className: "block text-xs text-gray-500 mb-1" }, "Property Tax Rate (% / year)"),
                        React.createElement("input", { type: "number", value: formData.propertyTaxRate, onChange: (e) => handleChange('propertyTaxRate', Number(e.target.value)), className: "w-full border border-gray-300 rounded px-2 py-1 text-sm", min: 0, step: 0.01 })),
                    React.createElement("label", { className: "text-sm text-gray-700" },
                        React.createElement("span", { className: "block text-xs text-gray-500 mb-1" }, "Insurance (per year)"),
                        React.createElement("input", { type: "number", value: formData.insuranceAnnual, onChange: (e) => handleChange('insuranceAnnual', Number(e.target.value)), className: "w-full border border-gray-300 rounded px-2 py-1 text-sm", min: 0, step: 50 })),
                    React.createElement("label", { className: "text-sm text-gray-700" },
                        React.createElement("span", { className: "block text-xs text-gray-500 mb-1" }, "HOA (per month)"),
                        React.createElement("input", { type: "number", value: formData.hoaMonthly, onChange: (e) => handleChange('hoaMonthly', Number(e.target.value)), className: "w-full border border-gray-300 rounded px-2 py-1 text-sm", min: 0, step: 10 }))),
                React.createElement("div", { className: "text-xs text-gray-500" },
                    "Loan Amount: ",
                    React.createElement("span", { className: "font-medium text-gray-700" }, formatCurrency(loanAmount)))))));
}
;
export default MortgageCalculator;
